import {
  resolveAccountReliabilityDisplay,
  type AccountReliabilityDisplay,
  type AccountReliabilityInput,
} from './resolveAccountReliabilityDisplay';

export type UserReliabilityAccount = AccountReliabilityInput & {
  platform?: string | null;
  username?: string | null;
};

export type UserReliabilityListDisplay =
  | { kind: 'blank' }
  | { kind: 'warning'; score: number }
  | { kind: 'scores'; parts: { platformLabel: string; score: number }[] };

const PLATFORM_LABELS: Record<string, string> = {
  instagram: 'IG',
  tiktok: 'TT',
  youtube: 'YT',
};

const PLATFORM_ORDER = ['instagram', 'tiktok', 'youtube'];

function platformLabel(platform: string | null | undefined): string {
  if (!platform) return '';
  return PLATFORM_LABELS[platform] || platform.toUpperCase();
}

function platformOrder(platform: string | null | undefined): number {
  const index = PLATFORM_ORDER.indexOf(platform || '');
  return index === -1 ? PLATFORM_ORDER.length : index;
}

export function resolveUserReliabilityListDisplay(
  accounts: UserReliabilityAccount[] | null | undefined,
): UserReliabilityListDisplay {
  const items = accounts || [];
  const displays = items.map((account) => ({
    account,
    display: resolveAccountReliabilityDisplay(account),
  }));

  const warnings = displays.filter(
    (item): item is { account: UserReliabilityAccount; display: Extract<AccountReliabilityDisplay, { kind: 'warning' }> } => (
      item.display.kind === 'warning'
    ),
  );
  if (warnings.length > 0) {
    return {
      kind: 'warning',
      score: Math.min(...warnings.map((item) => item.display.score)),
    };
  }

  const scored = displays.filter(
    (item): item is { account: UserReliabilityAccount; display: Extract<AccountReliabilityDisplay, { kind: 'score' }> } => (
      item.display.kind === 'score'
    ),
  );
  const parts = scored
    .sort((left, right) => (
      platformOrder(left.account?.platform) - platformOrder(right.account?.platform)
    ))
    .map((item) => ({
      platformLabel: platformLabel(item.account?.platform),
      score: item.display.score,
    }));

  if (parts.length === 0) {
    return { kind: 'blank' };
  }

  return { kind: 'scores', parts };
}
